<template>
	<div class="db2-note-wrap">
		<div class="db2-note" v-if="!isLog">{{ body || 'Empty note' }}</div>
		<div class="db2-log" v-else>
			<div class="db2-log-list" ref="logList">
				<div class="db2-log-empty" v-if="!entries.length">No log entries yet</div>
				<div class="db2-log-item" v-for="row in entries" :key="row.id">
					<div class="db2-log-meta">
						<span class="db2-log-when">{{ formatWhen(row.at) }}</span>
						<span class="db2-log-who" v-if="row.userName">{{ row.userName }}</span>
					</div>
					<div class="db2-log-text">{{ row.text }}</div>
				</div>
			</div>
			<div class="db2-log-add" v-if="canEdit" @mousedown.stop>
				<HaqTextField
					v-model="draftText"
					hide-details
					placeholder="Add a note"
					@keydown.enter.native.prevent="addEntry"
				/>
				<v-btn class="haq-btn haq-btn-primary" :disabled="!canSubmit" @click="addEntry">Add</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import HaqTextField from '@/components/form/HaqTextField.vue';

/**
 * Local date/time for a stored ISO timestamp.
 * @param {string} iso
 * @returns {string}
 */
function formatWhen(iso) {
	const d = new Date(iso);
	if (isNaN(d.getTime())) return '';
	return d.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
}

/**
 * Stored text / note card. Note mode is a single body; log mode is timestamped rows.
 */
export default {
	name: 'TextCard',
	components: { HaqTextField },
	props: {
		card: { type: Object, required: true },
		timePeriod: { type: String, default: 'week' },
		canEdit: { type: Boolean, default: false }
	},
	data() {
		return { draftText: '' };
	},
	computed: {
		/**
		 * Whether this card is a timestamped log.
		 * @returns {boolean}
		 */
		isLog() {
			return (this.card.config && this.card.config.mode) === 'log';
		},
		/**
		 * Note body (note mode).
		 * @returns {string}
		 */
		body() {
			return (this.card.config && this.card.config.body) || '';
		},
		/**
		 * Log rows in chronological order.
		 * @returns {Array<{ id: string, at: string, text: string, userName?: string }>}
		 */
		entries() {
			const rows = (this.card.config && this.card.config.entries) || [];
			return Array.isArray(rows) ? rows : [];
		},
		/**
		 * Whether the composer has text to add.
		 * @returns {boolean}
		 */
		canSubmit() {
			return !!String(this.draftText || '').trim();
		}
	},
	watch: {
		entries() {
			this.scrollLogToEnd();
		}
	},
	mounted() {
		this.scrollLogToEnd();
	},
	methods: {
		/**
		 * Format an ISO timestamp for the log meta line.
		 * @param {string} iso
		 * @returns {string}
		 */
		formatWhen,
		/**
		 * Keep the newest log row in view.
		 */
		scrollLogToEnd() {
			this.$nextTick(() => {
				const el = this.$refs.logList;
				if (el) el.scrollTop = el.scrollHeight;
			});
		},
		/**
		 * Append the composer text as a log row.
		 */
		addEntry() {
			const text = String(this.draftText || '').trim();
			if (!this.canEdit || !this.isLog || !text) return;
			this.$emit('append-log', { uniqueId: this.card.uniqueId, text: text });
			this.draftText = '';
		}
	}
};
</script>
